<template>
  <BasePopup
    ref="popup"
    title="What's new and features"
    class="changelog-popup"
    :fullscreen="true"
  >
    <div ref="containerRef" class="changelog-popup__container">
      <transition name="fade" mode="out-in" @enter="onTransitionEnd">
        <div v-if="selectedItem" key="detail" class="changelog-popup__details">
          <Item :item="selectedItem" :isDetail="true" :onBack="goBack" />
        </div>
        <div v-else key="list">
          <Item
            v-for="item in view.data"
            :key="item.id"
            :item="item"
            @click="selectItem(item)"
          />
        </div>
      </transition>
    </div>
  </BasePopup>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import type { ChangelogView } from '@/models/changelog'
import BasePopup from '@/components/ui/BasePopup.vue'
import Item from './Item.vue'
import { backend } from '@/services/backend'
import type { Changelog } from '@/models/interfaces'
import * as Mixpanel from '@/analytics/mixpanel/mixpanel'
const props = defineProps<{
  view: ChangelogView
}>()

// Note: in vue 3.5 we need to switch to useTemplateRef
const popup = ref<InstanceType<typeof BasePopup> | null>(null)

const selectedId = ref<string | null>(null)
const lastScrollPosition = ref<number>(0)
const containerRef = ref<HTMLElement | null>(null)

async function selectItem(item: Changelog): Promise<void> {
  // Store scroll position before any transition
  if (containerRef.value) {
    lastScrollPosition.value = containerRef.value.scrollTop
  }

  selectedId.value = item.id

  Mixpanel.trackChangelogItemClick(item)

  // If the item is historical or already read, we don't need to mark it as read
  if (item.is_historical || item.is_read) {
    return
  }

  await backend.markReadChangelog(item.id)

  // Update the item in the view
  item.is_read = true
  props.view.refreshIfNeeded(true)
}

const selectedItem = computed(() => {
  return props.view.data.find((item) => item.id === selectedId.value)
})

function show(): void {
  popup.value?.show()
}

function hide(): void {
  popup.value?.hide()
}

function goBack(): void {
  selectedId.value = null
}

// Handle transition end to restore scroll position
function onTransitionEnd(): void {
  if (containerRef.value) {
    // If we're in list view (selectedId is null), restore scroll position
    // Otherwise (in detail view), scroll to top
    containerRef.value.scrollTo({
      top: selectedId.value === null ? lastScrollPosition.value : 0,
    })
  }
}

defineExpose({
  show,
  hide,
})
</script>

<style lang="scss" scoped>
.changelog-popup {
  --input-border-color-hover: #{$c-border-5};
  --popup-width: #{$w-max-content-width};

  :deep(.popup__window) {
    border: 1px solid $c-border-4;
    padding: 0;
  }

  :deep(.popup__title) {
    margin-bottom: 0;
    font-size: 24px;
    line-height: 32px;
  }

  :deep(.popup__close) {
    top: 20px;
  }

  :deep(.popup__header) {
    padding: 30px 48px;
    margin-bottom: 0;
    text-align: center;
    border-color: $c-border;
    background: $c-bg;
  }

  &__container {
    max-height: 70vh;
    overflow-y: auto;
    padding: 80px;

    @media only screen and (max-width: $w-tablet-s) {
      padding: 24px;
    }

    @media (max-width: $w-medium-s) {
      max-height: none;
    }
  }

  &__details {
    min-height: 60svh;
  }
}

.night-mode .changelog-popup {
  --input-border-color: #{$c-black-9};
  --input-border-color-hover: #{$c-border-6};

  :deep(.popup__content) {
    border-color: $c-dark-3;
  }

  :deep(.popup__title-box) {
    border-color: $c-dark-3;
    background: $c-dark-2;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
